import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const TITLE_LINES = ["Aménagement et", "rénovation intérieure", "en Seine-et-Marne"];
const EASE = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-32 lg:pt-40 pb-20 lg:pb-28">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
        <div className="lg:col-span-6">
          <motion.div
            className="flex items-center gap-4 mb-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.35, duration: 0.8 }}
          >
            <span className="h-px w-12 bg-sable" aria-hidden="true" />
            <span className="font-accent text-xs font-medium tracking-[0.32em] uppercase text-anthracite/60" data-testid="hero-eyebrow">
              Aménagement &amp; rénovation intérieure — Seine-et-Marne
            </span>
          </motion.div>

          <h1 className="font-heading font-semibold text-nuit tracking-tight leading-[1.06] text-4xl sm:text-5xl lg:text-6xl" data-testid="hero-title">
            {TITLE_LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.95, delay: 1.45 + i * 0.14, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            className="mt-8 border-l-2 border-sable pl-5 font-heading text-lg md:text-xl font-semibold text-nuit"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.0, duration: 0.8, ease: EASE }}
            data-testid="hero-signature"
          >
            Le seuil entre l’existant et le nouvel intérieur.
          </motion.p>

          <motion.p
            className="mt-8 text-base md:text-lg leading-relaxed text-anthracite/80 max-w-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.15, duration: 0.8, ease: EASE }}
            data-testid="hero-subtitle"
          >
            Isolation, plaquisterie et aménagement intérieur réalisés avec méthode, précision et fiabilité.
          </motion.p>
          <motion.p
            className="mt-4 text-sm md:text-base leading-relaxed text-anthracite/60 max-w-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.3, duration: 0.8, ease: EASE }}
            data-testid="hero-text"
          >
            LIMEN réalise vos travaux de plaquisterie, d’isolation intérieure et d’aménagement.
            Cloisons, doublages, faux plafonds ou habillages sur mesure : nous étudions les contraintes
            de votre intérieur pour définir des ouvrages adaptés à votre projet. Notre méthode repose sur
            une visite technique, un devis détaillé, la préparation du chantier et un suivi jusqu’à la réception.
          </motion.p>

          <motion.div
            className="mt-10 flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.35, duration: 0.8, ease: EASE }}
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollToId("#contact")}
              className="bg-nuit px-8 py-4 font-accent text-sm font-medium tracking-[0.12em] uppercase text-casse hover:bg-anthracite transition-colors duration-300"
            >
              Demander une étude
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollToId("#realisations")}
              className="border border-nuit/25 px-8 py-4 font-accent text-sm font-medium tracking-[0.12em] uppercase text-nuit hover:border-sable-deep hover:text-sable-deep transition-colors duration-300"
            >
              Voir nos réalisations
            </button>
          </motion.div>
        </div>

        <div className="lg:col-span-6 relative">
          <motion.div
            className="absolute -bottom-5 -left-5 h-full w-full border border-sable hidden lg:block"
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.3, duration: 1 }}
          />
          <motion.div
            className="relative overflow-hidden aspect-[4/3] lg:aspect-[5/4]"
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ delay: 1.5, duration: 1.2, ease: EASE }}
          >
            <motion.img
              src="/images/renovation-interieure-seine-et-marne.png"
              alt="Intérieur rénové par LIMEN, entreprise de rénovation en Seine-et-Marne — mur TV architectural et corniche LED"
              className="absolute inset-0 h-[116%] w-full object-cover"
              style={{ y: imgY }}
              data-testid="hero-image"
            />
          </motion.div>
          <motion.p
            className="mt-4 font-accent text-[11px] tracking-[0.24em] uppercase text-anthracite/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.5, duration: 0.8 }}
          >
            Rénovation intérieure — ouvrage terminé
          </motion.p>
        </div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("#engagements")}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-anthracite/40 hover:text-nuit transition-colors duration-300"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.8, duration: 0.8 }}
        aria-label="Faire défiler"
      >
        <span className="font-accent text-[10px] tracking-[0.3em] uppercase">Découvrir</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown size={16} strokeWidth={1.5} />
        </motion.span>
      </motion.button>
    </section>
  );
};
